import {FC, useState} from "react";
import {App, Button} from "antd";
import {ThunderboltOutlined} from "@ant-design/icons";
import {useDispatch, useSelector} from "react-redux";
import AxiosServices from "../Services/axiosServices";
import {HasMatchStarted, IMatch} from "../Interfaces/IMatch";
import {IUserBets} from "../Interfaces/IUserBets";
import {AppDispatch, RootState} from "../store";
import {useAuth} from "../config/AuthContext";
import {useGlobal} from "../config/GlobalContext";
import {fetchUserBets} from "./betsSlice";

// Poisson(lambda=1.3) sampler via Knuth's algorithm, clamped to [0, 20].
// Tuned on the 2022 World Cup team-score distribution.
const LAMBDA = 1.3;
function randomScore(): number {
    const L = Math.exp(-LAMBDA);
    let k = 0;
    let p = 1;
    do {
        k++;
        p *= Math.random();
    } while (p > L);
    return Math.min(20, k - 1);
}

const RandomBetButton: FC = () => {
    const {authUser, isLoggedIn} = useAuth();
    const {currentCmpEdt} = useGlobal();
    const dispatch = useDispatch<AppDispatch>();
    const allBets = useSelector((state: RootState) => state.bets.userBets);
    const [fillingRandom, setFillingRandom] = useState(false);
    const {message} = App.useApp();

    const showRandomBetButton = isLoggedIn && !!authUser?.randomBet;
    if (!showRandomBetButton) return null;

    const handleFillRandom = async () => {
        if (fillingRandom || !isLoggedIn || !authUser?.username || !currentCmpEdt) return;
        setFillingRandom(true);
        try {
            const matches: IMatch[] = await new Promise<IMatch[]>((resolve, reject) => {
                AxiosServices.getMatches(resolve, currentCmpEdt).catch(reject);
            });
            const tasks: Promise<any>[] = [];
            for (const match of matches) {
                if (!match.team_idt_Dom || !match.team_idt_Ext) continue;
                if (HasMatchStarted(match)) continue;
                const hasDom = allBets.some((b: IUserBets) => b.matchteam_idt === match.MatchTeam_Idt_Dom);
                const hasExt = allBets.some((b: IUserBets) => b.matchteam_idt === match.MatchTeam_Idt_Ext);
                if (!hasDom) tasks.push(AxiosServices.SaveUserBet(match.MatchTeam_Idt_Dom, randomScore(), true));
                if (!hasExt) tasks.push(AxiosServices.SaveUserBet(match.MatchTeam_Idt_Ext, randomScore(), true));
            }
            if (tasks.length === 0) {
                message.info("Aucun pari à compléter — tous les scores sont déjà saisis.");
                return;
            }
            await Promise.all(tasks);
            await dispatch(fetchUserBets({competition: currentCmpEdt, usr: authUser.username})).unwrap();
            message.success(`${tasks.length} score(s) aléatoire(s) enregistré(s).`);
        } catch (e: any) {
            message.error(`Échec de la saisie aléatoire : ${e?.message ?? 'inconnue'}`);
        } finally {
            setFillingRandom(false);
        }
    };

    return (
        <Button
            size="small"
            type="primary"
            icon={<ThunderboltOutlined/>}
            loading={fillingRandom}
            onClick={handleFillRandom}
            title="Compléter les paris vides avec un score aléatoire"
        >
            Aléatoire
        </Button>
    );
};

export default RandomBetButton;
